import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import {
  registerUser,
  fetchAndSaveUserDetails,
  checkFridgeCode,
} from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import { termsContent } from "../content/terms";
import { privacyContent } from "../content/privacy";
import { getTenantFromHost } from "../services/utils/host";

// Animations
const gradientShift = keyframes`
  0% { background-position: 0 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const RegistrationScreen = () => {
  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [personalNumber, setPersonalNumber] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [fridgeName, setFridgeName] = useState("");
  const [fridgeCode, setFridgeCode] = useState("");
  const [customerId, setCustomerId] = useState("");

  const [errors, setErrors] = useState({});
  const [isFridgeCodeValid, setIsFridgeCodeValid] = useState(true);
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalData, setModalData] = useState(null);

  const { login } = useAuth();
  const navigate = useNavigate();

  // Tenant check for label
  const isBoxmart = getTenantFromHost() === "boxmart";

  // Handlers
  const handleFridgeCodeChange = (e) => {
    const code = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    setFridgeCode(code);
  };

  const handlePersonalNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setPersonalNumber(value);
  };

  const handleFetchFridgeName = async () => {
    if (fridgeCode.length > 0 && /^[A-Za-z0-9]+$/.test(fridgeCode)) {
      try {
        const result = await checkFridgeCode(fridgeCode);
        if (result && result.fridgeName) {
          setFridgeName(result.fridgeName);
          setCustomerId(result.customerId || "");
          setIsFridgeCodeValid(true);
          if (result.fridgeId != null) {
            localStorage.setItem("selectedFridgeId", String(result.fridgeId));
          }
          return;
        }
      } catch (err) {
        console.warn("checkFridgeCode failed:", err);
      }
    }
    // Fallback: reset and mark invalid
    setFridgeName("");
    setIsFridgeCodeValid(false);
    localStorage.removeItem("selectedFridgeId");
  };

  const validateFields = () => {
    const nextErrors = {};
    const personalNumberRegex = /^\d{12}$/;

    if (!firstName) nextErrors.firstName = "Förnamn är obligatoriskt";
    if (!lastName) nextErrors.lastName = "Efternamn är obligatoriskt";
    if (!personalNumber || !personalNumberRegex.test(personalNumber)) {
      nextErrors.personalNumber =
        "Giltigt 12-siffrigt personnummer krävs (YYYYMMDDXXXX)";
    }
    if (!email) nextErrors.email = "E-postadress är obligatorisk";
    if (!phone) nextErrors.phone = "Telefonnummer är obligatoriskt";
    if (!fridgeCode) nextErrors.fridgeCode = "Fridge code är obligatoriskt";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleRegistration = async () => {
    if (!validateFields() || !isConsentChecked || !isFridgeCodeValid) return;

    setIsSubmitting(true);
    const userData = {
      firstName,
      lastName,
      personalNumber,
      email,
      phone,
      fridgeName,
      fridgeCode,
      role: "user",
      customerId,
    };

    try {
      const result = await registerUser(userData);
      if (result?.success) {
        const userDetails = await fetchAndSaveUserDetails(result.token);
        if (userDetails) {
          localStorage.setItem("customerId", customerId);
          login(userDetails, result.token);
          navigate("/products");
        } else {
          console.error("Registration succeeded but fetching user details failed.");
        }
      } else {
        console.error("Registration failed:", result);
      }
    } catch (error) {
      console.error("Error during registration:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openModal = (type) => {
    if (type === "terms") {
      setModalData({ title: "Allmänna villkor", content: termsContent });
    } else if (type === "privacy") {
      setModalData({ title: "Integritetspolicy", content: privacyContent });
    }
  };

  const closeModal = () => setModalData(null);

  // UI
  return (
    <Container>
      <FormCard>
        <FormContent>
          <TopSection>
            <FormTitle>Skapa ditt konto</FormTitle>
            <FormSubtitle>Fyll i dina uppgifter för att komma igång</FormSubtitle>
          </TopSection>

          <FormGrid>
            <InputGroup>
              <Label>Förnamn</Label>
              <Input
                type="text"
                placeholder="Ange ditt förnamn"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                isInvalid={!!errors.firstName}
              />
              {errors.firstName && <ErrorMessage>{errors.firstName}</ErrorMessage>}
            </InputGroup>

            <InputGroup>
              <Label>Efternamn</Label>
              <Input
                type="text"
                placeholder="Ange ditt efternamn"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                isInvalid={!!errors.lastName}
              />
              {errors.lastName && <ErrorMessage>{errors.lastName}</ErrorMessage>}
            </InputGroup>

            <InputGroup fullWidth>
              <Label>Personnummer</Label>
              <Input
                type="text"
                placeholder="YYYYMMDDXXXX"
                value={personalNumber}
                onChange={handlePersonalNumberChange}
                isInvalid={!!errors.personalNumber}
                maxLength={12}
              />
              {errors.personalNumber && <ErrorMessage>{errors.personalNumber}</ErrorMessage>}
            </InputGroup>

            <InputGroup fullWidth>
              <Label>E-postadress</Label>
              <Input
                type="email"
                placeholder="din@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                isInvalid={!!errors.email}
              />
              {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
            </InputGroup>

            <InputGroup fullWidth>
              <Label>Telefonnummer</Label>
              <Input
                type="tel"
                placeholder="070-123 45 67"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                isInvalid={!!errors.phone}
              />
              {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
            </InputGroup>

            <InputGroup fullWidth>
              <Label>{isBoxmart ? "LAGETS FÖRSÄLJNINGSKOD" : "Enhet-ID"}</Label>
              <FridgeCodeContainer>
                <Input
                  type="text"
                  placeholder={isBoxmart ? "EX. BRAGEPOJKAR12" : "Ange enhetens ID"}
                  value={fridgeCode}
                  onChange={handleFridgeCodeChange}
                  isInvalid={!!errors.fridgeCode || !isFridgeCodeValid}
                  style={{ textTransform: "uppercase" }}
                />
              </FridgeCodeContainer>
              {errors.fridgeCode && <ErrorMessage>{errors.fridgeCode}</ErrorMessage>}
            </InputGroup>

            <InputGroup fullWidth>
              <Label>Enhetsnamn</Label>
              <Label>{isBoxmart ? "LAGNAMN" : "Enhetsnamn"}</Label>
              <Input
                readOnly
                type="text"
                placeholder="Enhetsnamn"
                value={fridgeName}
                onFocus={handleFetchFridgeName}
                onChange={(e) => setFridgeName(e.target.value)}
              />
            </InputGroup>
          </FormGrid>

          <ConsentSection>
            <ConsentContainer>
              <Checkbox
                type="checkbox"
                id="consentCheckbox"
                checked={isConsentChecked}
                onChange={(e) => setIsConsentChecked(e.target.checked)}
              />
              <ConsentLabel htmlFor="consentCheckbox">
                Jag godkänner Ziplinks {" "}
                <ConsentLink onClick={() => openModal("terms")}>
                  Allmänna villkor
                </ConsentLink>{" "}
                och {" "}
                <ConsentLink onClick={() => openModal("privacy")}>
                  Integritetspolicy
                </ConsentLink>
                .
              </ConsentLabel>
            </ConsentContainer>
          </ConsentSection>

          <SubmitButton onClick={handleRegistration} disabled={!isConsentChecked || isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner small />
                Registrerar...
              </>
            ) : (
              "Registrera"
            )}
          </SubmitButton>
        </FormContent>
      </FormCard>

      {modalData && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>{modalData.title}</h2>
              <CloseButton onClick={closeModal}>×</CloseButton>
            </ModalHeader>
            <ModalBody dangerouslySetInnerHTML={{ __html: modalData.content }} />
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default RegistrationScreen;

// Styled components
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%);
  background-size: 400% 400%;
  animation: ${gradientShift} 8s ease-in-out infinite;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow-x: hidden;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const FormCard = styled.div`
  width: 100%;
  max-width: 550px;
  background: #ffffff;
  border-radius: 24px;
  padding: 0;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  margin: 0 auto;
  min-width: 320px;
`;

const FormContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 40px 32px 32px 32px;
  width: 100%;
  box-sizing: border-box;
`;

const TopSection = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const FormTitle = styled.h1`
  color: #1e293b;
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
  letter-spacing: -0.5px;
`;

const FormSubtitle = styled.p`
  color: #64748b;
  font-size: 14px;
  margin: 0 0 24px 0;
  font-weight: 400;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 32px;
`;

const InputGroup = styled.div`
  ${(props) => props.fullWidth && "grid-column: 1 / -1;"}
`;

const Label = styled.label`
  display: block;
  color: #374151;
  font-weight: 500;
  margin-bottom: 8px;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 12px;
  padding: 16px 20px;
  font-size: 16px;
  color: #1e293b;
  background: rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
  box-sizing: border-box;

  ${(props) =>
    props.isInvalid && `
      border-color: rgba(239, 68, 68, 0.5);
      background: rgba(254, 242, 242, 0.8);
    `}

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: #252525;
    background: rgba(255, 255, 255, 0.95);
    transform: translateY(-2px);
  }

  ${(props) =>
    props.isInvalid && `
      &:focus {
        border-color: rgba(239, 68, 68, 0.8);
        background: rgba(254, 242, 242, 0.9);
        box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
      }
    `}

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px rgba(255, 255, 255, 0.95) inset;
    -webkit-text-fill-color: #1e293b;
    background-color: rgba(255, 255, 255, 0.95);
    transition: background-color 5000s ease-in-out 0s;
  }
`;

const FridgeCodeContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Spinner = styled.div`
  width: ${(props) => (props.small ? "16px" : "20px")};
  height: ${(props) => (props.small ? "16px" : "20px")};
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top: 2px solid #000000;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const ConsentSection = styled.div`
  margin-bottom: 32px;
`;

const ConsentContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const Checkbox = styled.input`
  accent-color: #252525;
  transform: scale(1.2);
  margin-top: 2px;
`;

const ConsentLabel = styled.label`
  font-size: 14px;
  color: #64748b;
  line-height: 1.5;
  cursor: pointer;
`;

const ConsentLink = styled.span`
  color: #1e293b;
  cursor: pointer;
  text-decoration: underline;
  font-weight: 500;

  &:hover {
    color: #000;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: ${(props) =>
    props.disabled
      ? "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)"
      : "linear-gradient(135deg, #000000 0%, #1f2937 50%, #374151 100%)"};
  border: none;
  color: ${(props) => (props.disabled ? "#d1d5db" : "#ffffff")};
  border-radius: 12px;
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #1f2937 0%, #374151 50%, #4b5563 100%);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
    transform: translateY(-1px);
  }
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 13px;
  margin-top: 8px;
  padding: 8px 12px;
  background: rgba(254, 242, 242, 0.8);
  border-radius: 8px;
  border: 1px solid rgba(239, 68, 68, 0.2);

  &::before {
    content: '⚠ ';
    opacity: 0.8;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #fff;
  padding: 24px;
  border-radius: 16px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  h2 {
    font-size: 20px;
    color: #252525;
    margin: 0;
    font-weight: 600;
  }
`;

const ModalBody = styled.div`
  line-height: 1.6;
  font-size: 14px;
  color: #333;

  h2, h3 {
    margin-top: 20px;
    font-size: 16px;
    color: #252525;
    font-weight: 600;
  }

  p {
    margin: 12px 0;
  }

  a {
    color: #252525;
    text-decoration: underline;
    cursor: pointer;
  }

  a:hover {
    color: #000;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background: #f5f5f5;
    color: #252525;
  }
`;